import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  List,
  ListItem,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import { useQuery } from "@apollo/client";
import { GET_TRAINING_MODULES_LIBRARY } from "../../../apis/training-apis/trainingApis";
import { useTheme } from "@mui/material/styles";

export default function AddTrainingFromLibraryDialog({
  open,
  onClose,
  onAddTrainings,
}) {
  const [selectedTrainings, setSelectedTrainings] = useState([]);
  const [alreadyAddedTrainings, setAlreadyAddedTrainings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredModules, setFilteredModules] = useState([]);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const theme = useTheme();

  const { data, refetch, loading } = useQuery(GET_TRAINING_MODULES_LIBRARY, {
    variables: { page: 1, limit: 1000 },
  });

  const trainingModulesLibrary = useMemo(() => {
    return data?.getTrainingModulesLibrary?.data || [];
  }, [data]);

  useEffect(() => {
    if (open && trainingModulesLibrary?.length > 0) {
      const existingIds = trainingModulesLibrary
        .filter((module) => module?.is_added)
        .map((module) => module?.id);
      setSelectedTrainings(existingIds);
      setAlreadyAddedTrainings(existingIds);
      setFilteredModules(trainingModulesLibrary);
    }
  }, [open, trainingModulesLibrary]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredModules(trainingModulesLibrary);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredModules(
        trainingModulesLibrary.filter(
          (module) =>
            module?.title.toLowerCase().includes(query) ||
            module?.description.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, trainingModulesLibrary]);

  // Refetch when dialog opens
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open]);

  const handleToggle = (id) => {
    setSelectedTrainings((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const selectable = filteredModules
      .filter((m) => !m?.is_added)
      .map((m) => m.id);
    const selected = selectedTrainings.filter((id) => selectable.includes(id));
    if (selected.length === selectable.length) {
      const existingIds = filteredModules
        .filter((m) => m?.is_added)
        .map((m) => m.id);
      setSelectedTrainings(existingIds);
    } else {
      setSelectedTrainings([...new Set([...selectedTrainings, ...selectable])]);
    }
  };

  const handleAdd = async () => {
    setLoadingAdd(true);
    const toAdd = trainingModulesLibrary.filter(
      (m) =>
        selectedTrainings.includes(m.id) &&
        !alreadyAddedTrainings.includes(m.id)
    );

    try {
      await onAddTrainings(toAdd);
      setLoadingAdd(false);
      setSelectedTrainings([]);
      setSearchQuery("");
      refetch();
      onClose(); // only close after success
    } catch (error) {
      console.error("Error adding training courses:", error);
      setLoadingAdd(false);
    }
  };

  const clearFilters = () => setSearchQuery("");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "16px" }}>
        Add Trainings from Library
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading training courses...
            </Typography>
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontSize: "14px" }}
              >
                Select training courses from our library to add to your list.
                Already added ones are shown as checked and disabled.
              </Typography>
            </Alert>

            <Box sx={{ px: 2, py: 1 }}>
              <TextField
                fullWidth
                placeholder="Search training by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {searchQuery && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}
                >
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    {filteredModules?.length} training courses found
                  </Typography>
                  <Button
                    size="small"
                    onClick={clearFilters}
                    sx={{ fontSize: "12px" }}
                  >
                    Clear filters
                  </Button>
                </Box>
              )}

              {filteredModules?.length > 0 && (
                <Box sx={{ my: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filteredModules.every((m) =>
                          selectedTrainings.includes(m.id)
                        )}
                        indeterminate={
                          filteredModules.some((m) =>
                            selectedTrainings.includes(m.id)
                          ) &&
                          !filteredModules.every((m) =>
                            selectedTrainings.includes(m.id)
                          )
                        }
                        onChange={handleSelectAll}
                      />
                    }
                    label="Select All"
                    sx={{
                      "& .MuiFormControlLabel-label": { fontSize: "14px" },
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              <List>
                {filteredModules?.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <LocalLibraryOutlinedIcon
                      sx={{ fontSize: 40, color: "text.secondary", mb: 2 }}
                    />
                    <Typography
                      variant="h5"
                      color="textSecondary"
                      sx={{ mb: 1 }}
                    >
                      No training courses found
                    </Typography>
                  </Box>
                ) : (
                  filteredModules.map((module) => {
                    const isSelected = selectedTrainings.includes(module.id);
                    const isExisting = module?.is_added;
                    return (
                      <ListItem
                        key={module?.id}
                        sx={{
                          border: "1px solid",
                          borderColor: theme.customColors.border.accent,
                          borderRadius: "10px",
                          mb: 1.5,
                          background: isSelected
                            ? theme.customColors.background.info
                            : theme.customColors.background.main,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                          cursor: isExisting ? "default" : "pointer",
                          opacity: isExisting ? 0.6 : 1,
                          "&:hover": !isExisting && {
                            backgroundColor: theme.customColors.background.info,
                          },
                        }}
                        onClick={() => !isExisting && handleToggle(module.id)}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isExisting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(module?.id);
                          }}
                          sx={{ p: 0.5, mr: 2 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight="bold" fontSize="14px">
                            {module?.title}
                          </Typography>
                          <Typography fontSize="12px" color="text.secondary">
                            {module?.description}
                          </Typography>
                          {isExisting && (
                            <Chip
                              label="Already Added"
                              size="small"
                              sx={{
                                backgroundColor:
                                  theme.customColors.status.completed.bg,
                                color: theme.customColors.status.success.text,
                                fontSize: "10px",
                                borderRadius: "8px",
                                fontWeight: 600,
                                ml: 1,
                              }}
                            />
                          )}
                        </Box>
                      </ListItem>
                    );
                  })
                )}
              </List>
            </Box>
          </>
        )}
      </DialogContent>

      {!loading && (
        <DialogActions>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ fontSize: "14px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            startIcon={!loadingAdd && <AddIcon sx={{ fontSize: 16 }} />}
            disabled={
              loadingAdd ||
              selectedTrainings.filter(
                (id) =>
                  !trainingModulesLibrary.find((m) => m?.id === id)?.is_added
              )?.length === 0
            }
            sx={{ fontSize: "14px", minWidth: "180px" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {loadingAdd ? (
                <CircularProgress size={18} />
              ) : (
                `Add Selected (${selectedTrainings.filter(
                  (id) =>
                    !trainingModulesLibrary.find((m) => m?.id === id)
                      ?.is_added
                )?.length
                })`
              )}
            </Box>
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
